import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";

import Sidebar from "@/components/Sidebar";
import { authApi } from "@/lib/adminApi";
import { useTheme } from "@/lib/theme";

/* ============================================================
   ROLE HELPERS
   ============================================================ */

function normalizeRole(role) {
  const value = String(role || "").trim().toUpperCase();

  const legacyRoles = {
    SUPER_ADMIN: "ADMIN",
    ADMIN: "ADMIN",

    PROJECT_MANAGER: "PM",
    "PROJECT MANAGER": "PM",
    PM: "PM",

    ASSISTANT_PROJECT_MANAGER: "APM",
    "ASSISTANT PROJECT MANAGER": "APM",
    APM: "APM",

    FIELD_OFFICER: "FIELD_OFFICER",
    "FIELD OFFICER": "FIELD_OFFICER",

    CIRCLE_INCHARGE: "CIRCLE_INCHARGE",
    "CIRCLE INCHARGE": "CIRCLE_INCHARGE",
  };

  return legacyRoles[value] || value;
}

/* ============================================================
   ROLE LABEL
   ============================================================ */

function getRoleLabel(role) {
  const normalized = normalizeRole(role);

  const labels = {
    ADMIN: "Admin",
    PM: "Project Manager",
    APM: "Assistant Project Manager",
    FIELD_OFFICER: "Field Officer",
    CIRCLE_INCHARGE: "Circle Incharge",
  };

  return labels[normalized] || normalized || "User";
}

/* ============================================================
   TITLE CASE HELPER
   ============================================================ */

function toTitleCase(value) {
  const text = String(value || "").trim();

  if (!text) {
    return "";
  }

  return text
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

/* ============================================================
   DISPLAY NAME
   ============================================================ */

function formatDisplayName(user) {
  const rawName =
    user?.name ||
    user?.full_name ||
    user?.fullName ||
    user?.username ||
    user?.email ||
    "User";

  return toTitleCase(rawName);
}

/* ============================================================
   PAGE TITLE
   ============================================================ */

function getPageTitle(pathname, suppliedTitle) {
  /*
   * If page supplies its own title, use it.
   */
  if (suppliedTitle && String(suppliedTitle).trim()) {
    return toTitleCase(suppliedTitle);
  }

  /*
   * Fallback page titles.
   */
  const pathTitles = [
    {
      match: "/admin/users",
      title: "User Management",
    },
    {
      match: "/admin/resources",
      title: "Master Data",
    },
    {
      match: "/admin/activity",
      title: "Activity Log",
    },
    {
      match: "/expenses",
      title: "Daily Expenses",
    },
    {
      match: "/hrms",
      title: "HRMS & Payroll",
    },
    {
      match: "/billing",
      title: "Vendor Billing",
    },
    {
      match: "/discom",
      title: "Discom",
    },
    {
      match: "/inventory",
      title: "Inventory",
    },
    {
      match: "/dt-inspection",
      title: "DT Inspection",
    },
    {
      match: "/",
      title: "Dashboard",
    },
  ];

  const found = pathTitles.find((item) => {
    if (item.match === "/") {
      return pathname === "/";
    }

    return (
      pathname === item.match ||
      pathname.startsWith(`${item.match}/`)
    );
  });

  return found?.title || "Prathvi Power Solutions";
}

/* ============================================================
   SIDEBAR ACTIVE ROUTE
   ============================================================ */

function getActiveRoute(pathname) {
  if (pathname === "/") {
    return "home";
  }

  if (pathname.startsWith("/expenses")) {
    return "expenses";
  }

  if (pathname.startsWith("/hrms")) {
    return "hrms";
  }

  if (pathname.startsWith("/billing")) {
    return "billing";
  }

  if (pathname.startsWith("/discom")) {
    return "discom";
  }

  if (pathname.startsWith("/inventory")) {
    return "inventory";
  }

  if (pathname.startsWith("/dt-inspection")) {
    return "dt-inspection";
  }

  if (pathname.startsWith("/admin/resources")) {
    return "master-data";
  }

  if (pathname.startsWith("/admin/users")) {
    return "user-management";
  }

  if (pathname.startsWith("/admin/activity")) {
    return "activity-log";
  }

  return "";
}

/* ============================================================
   ADMIN LAYOUT
   ============================================================ */

export default function AdminLayout({
  children,
  title = "",
  subtitle = "",
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const { theme, toggle } = useTheme();

  const [user, setUser] = useState(() =>
    authApi.getCachedUser()
  );

  const [mobileNav, setMobileNav] = useState(false);

  /* ==========================================================
     LOAD CURRENT USER
     ========================================================== */

  useEffect(() => {
    let mounted = true;

    const loadUser = async () => {
      try {
        const currentUser = await authApi.me();

        if (mounted && currentUser) {
          setUser(currentUser);

          if (typeof authApi.cacheUser === "function") {
            authApi.cacheUser(currentUser);
          }
        }
      } catch (error) {
        /*
         * Cached user will continue to be used
         * if current-user refresh fails.
         */
        console.error(
          "Unable to refresh current user:",
          error
        );
      }
    };

    if (authApi.hasToken()) {
      loadUser();
    }

    return () => {
      mounted = false;
    };
  }, []);

  /* ==========================================================
     USER INFORMATION
     ========================================================== */

  const normalizedRole = normalizeRole(user?.role);

  const displayName = useMemo(
    () => formatDisplayName(user),
    [user]
  );

  const currentRoleLabel = useMemo(
    () => getRoleLabel(normalizedRole),
    [normalizedRole]
  );

  /* ==========================================================
     PAGE TITLE
     ========================================================== */

  const pageTitle = useMemo(() => {
    return getPageTitle(
      location.pathname,
      title
    );
  }, [location.pathname, title]);

  /* ==========================================================
     ACTIVE SIDEBAR ROUTE
     ========================================================== */

  const activeRoute = useMemo(() => {
    return getActiveRoute(location.pathname);
  }, [location.pathname]);

  /* ==========================================================
     LOGOUT
     ========================================================== */

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      if (typeof authApi.clearSession === "function") {
        authApi.clearSession();
      }

      navigate("/admin/login", {
        replace: true,
      });
    }
  };

  /* ==========================================================
     CLOSE MOBILE MENU
     ========================================================== */

  const closeMobileMenu = () => {
    setMobileNav(false);
  };

  /* ==========================================================
     RENDER
     ========================================================== */

  return (
    <div
      className="
        min-h-screen
        bg-background
        text-foreground
      "
    >
      <div className="flex min-h-screen">

        {/* ====================================================
            DESKTOP SIDEBAR
           ==================================================== */}

        <div className="hidden lg:block">
          <Sidebar
            theme={theme}
            onToggleTheme={toggle}
            activeRoute={activeRoute}
          />
        </div>

        {/* ====================================================
            MOBILE SIDEBAR
           ==================================================== */}

        {mobileNav && (
          <div
            className="
              fixed
              inset-0
              z-50
              flex
              lg:hidden
            "
          >
            <div
              className="
                w-[280px]
                max-w-[85vw]
                bg-background
                border-r
                border-border
                shadow-2xl
              "
            >
              <Sidebar
                mobileOverlay
                theme={theme}
                onToggleTheme={toggle}
                activeRoute={activeRoute}
                onClose={closeMobileMenu}
              />
            </div>

            <button
              type="button"
              aria-label="Close menu"
              className="
                flex-1
                bg-black/50
                backdrop-blur-[1px]
              "
              onClick={closeMobileMenu}
            />
          </div>
        )}

        {/* ====================================================
            MAIN CONTENT
           ==================================================== */}

        <main
          className="
            flex-1
            min-w-0
            min-h-screen
          "
        >

          {/* ==================================================
              TOP HEADER
              HOME DASHBOARD STYLE
             ================================================== */}

          <header
            className="
              sticky
              top-0
              z-30
              glass
              glass-dark
              dark:glass-dark
              [.light_&]:glass-light
              border-b
              border-border
            "
          >
            <div
              className="
                px-4
                sm:px-8
                min-h-16
                flex
                items-center
                gap-4
              "
            >

              {/* ----------------------------------------------
                  MOBILE MENU BUTTON
                 ---------------------------------------------- */}

              <button
                type="button"
                onClick={() => setMobileNav(true)}
                className="
                  lg:hidden
                  w-9
                  h-9
                  shrink-0
                  rounded-lg
                  bg-muted
                  flex
                  items-center
                  justify-center
                  hover:bg-muted/80
                  transition-colors
                "
                aria-label="Open menu"
              >
                {mobileNav ? (
                  <X className="w-4 h-4" />
                ) : (
                  <Menu className="w-4 h-4" />
                )}
              </button>

              {/* ----------------------------------------------
                  LEFT
                  MODULE / PAGE NAME
                 ---------------------------------------------- */}

              <div className="min-w-0 flex-1">
                <div
                  className="
                    truncate
                    text-[15px]
                    sm:text-base
                    md:text-lg
                    font-heading
                    font-black
                    tracking-[0.04em]
                    text-foreground
                  "
                  title={pageTitle}
                >
                  {pageTitle}
                </div>
              </div>

              {/* ----------------------------------------------
                  RIGHT
                  WELCOME USER
                 ---------------------------------------------- */}

              <div
                className="
                  flex
                  flex-col
                  items-end
                  justify-center
                  min-w-0
                  shrink-0
                "
              >

                {/* USER NAME */}

                <div
                  className="
                    truncate
                    max-w-[220px]
                    sm:max-w-[360px]
                    text-xs
                    sm:text-sm
                    font-serif
                    font-semibold
                    tracking-wide
                    text-foreground
                    leading-tight
                  "
                  title={`Welcome Mr. ${displayName}`}
                >
                  Welcome Mr. {displayName}
                </div>

                {/* ROLE + LOGOUT */}

                <div
                  className="
                    flex
                    items-center
                    gap-2
                    mt-0.5
                  "
                >
                  <span
                    className="
                      text-[9px]
                      text-muted-foreground
                      tracking-wider
                    "
                  >
                    {currentRoleLabel}
                  </span>

                  <button
                    type="button"
                    onClick={handleLogout}
                    className="
                      inline-flex
                      items-center
                      gap-1
                      px-2
                      py-1
                      rounded-md
                      text-[10px]
                      sm:text-xs
                      font-semibold
                      text-foreground
                      hover:bg-muted
                      transition-colors
                    "
                  >
                    <LogOut className="w-3 h-3" />

                    <span className="hidden sm:inline">
                      Logout
                    </span>
                  </button>
                </div>
              </div>

            </div>
          </header>

          {/* ==================================================
              PAGE CONTENT
             ================================================== */}

          <section
            className="
              w-full
              px-4
              sm:px-6
              lg:px-8
              py-5
              sm:py-6
            "
          >
            {children}
          </section>

        </main>
      </div>
    </div>
  );
}